<template>
  <button
    :class="[
      'zl-button',
      `zl-button--${variant}`,
      `zl-button--${size}`,
      {
        'zl-button--glass': glass,
        'zl-button--gradient': gradient,
        'zl-button--rounded': rounded,
        'zl-button--shadow': shadow,
        'zl-button--hover-lift': hoverLift,
        'zl-button--loading': loading,
        'zl-button--long': long
      }
    ]"
    :style="getButtonStyle()"
    :type="htmlType"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="zl-button__loading">
      <svg class="zl-button__spinner" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
          <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
          <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
        </circle>
      </svg>
    </span>
    <span v-if="$slots.icon && !loading" class="zl-button__icon">
      <slot name="icon"></slot>
    </span>
    <span v-if="$slots.default" class="zl-button__content" :style="getButtonStyle()">
      <slot></slot>
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // 按钮变体
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'primary', 'secondary', 'success', 'warning', 'danger', 'info', 'login', 'glass', 'gradient'].includes(value)
  },
  // 按钮尺寸
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['mini', 'small', 'medium', 'large'].includes(value)
  },
  // 加载状态
  loading: {
    type: Boolean,
    default: false
  },
  // 禁用状态
  disabled: {
    type: Boolean,
    default: false
  },
  // 长按钮
  long: {
    type: Boolean,
    default: false
  },
  // HTML类型
  htmlType: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  // 毛玻璃效果
  glass: {
    type: Boolean,
    default: false
  },
  // 渐变效果
  gradient: {
    type: Boolean,
    default: false
  },
  // 圆角
  rounded: {
    type: Boolean,
    default: true
  },
  // 阴影
  shadow: {
    type: Boolean,
    default: true
  },
  // 悬停提升效果
  hoverLift: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['click'])

const getButtonStyle = () => {
  const colorMap = {
    primary: '#ffeb3b',      // 蓝紫色的反转色：亮黄色
    secondary: '#ffffff',    // 灰色的反转色：白色
    success: '#ff4500',      // 蓝绿色的反转色：橙红色
    warning: '#1a237e',      // 橙色的反转色：深蓝色
    danger: '#00e676',       // 红色的反转色：绿色
    info: '#ff9800',         // 蓝灰色的反转色：橙色
    login: '#4a148c',        // 淡蓝紫色的反转色：深紫色
    gradient: '#ffeb3b',     // 与primary相同
    default: '#2c3e50'       // 默认深色
  }
  
  let color = colorMap[props.variant] || colorMap.default
  
  if (props.glass) {
    color = 'rgba(44, 62, 80, 0.9)'
  }
  
  // 添加调试信息
  console.log(`按钮变体: ${props.variant}, 应用颜色: ${color}, 毛玻璃: ${props.glass}`)
  
  return {
    color: color,
    // 强制覆盖所有可能的颜色属性
    'color': color + ' !important',
    '--text-color': color,
    'text-color': color
  }
}

const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style>
/* 深度选择器强制覆盖ArcoVue样式 */
:deep(.zl-button) {
  color: inherit !important;
}

:deep(button.zl-button) {
  color: inherit !important;
}

/* 全局强制样式 */
.zl-button * {
  color: inherit !important;
}

button.zl-button * {
  color: inherit !important;
}

/* 基础按钮样式 */
.zl-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  transition: all 0.3s ease;
  border-radius: 8px;
  box-sizing: border-box;
}

/* 长按钮 */
.zl-button--long {
  width: 100%;
}

/* 加载状态 */
.zl-button--loading {
  pointer-events: none;
  opacity: 0.8;
}

.zl-button__loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.zl-button__spinner {
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.zl-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.zl-button__content {
  display: inline-flex;
  align-items: center;
}

/* 按钮变体 - 使用最强的选择器覆盖ArcoVue样式 */
button.zl-button.zl-button--default,
.zl-button.zl-button--default {
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  color: #2c3e50 !important;
}

button.zl-button.zl-button--primary,
.zl-button.zl-button--primary {
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  border: none !important;
  color: #ffeb3b !important; /* 蓝紫色的反转色：亮黄色 */
}

button.zl-button.zl-button--secondary,
.zl-button.zl-button--secondary {
  background: linear-gradient(135deg, #a8a8a8, #d0d0d0) !important;
  border: none !important;
  color: #ffffff !important; /* 灰色的反转色：白色 */
}

button.zl-button.zl-button--success,
.zl-button.zl-button--success {
  background: linear-gradient(135deg, #4facfe, #00f2fe) !important;
  border: none !important;
  color: #ff4500 !important; /* 蓝绿色的反转色：橙红色 */
}

button.zl-button.zl-button--warning,
.zl-button.zl-button--warning {
  background: linear-gradient(135deg, #ffa726, #ff7043) !important;
  border: none !important;
  color: #1a237e !important; /* 橙色的反转色：深蓝色 */
}

button.zl-button.zl-button--danger,
.zl-button.zl-button--danger {
  background: linear-gradient(135deg, #ff5722, #e91e63) !important;
  border: none !important;
  color: #00e676 !important; /* 红色的反转色：绿色 */
}

button.zl-button.zl-button--info,
.zl-button.zl-button--info {
  background: linear-gradient(135deg, #42a5f5, #78909c) !important;
  border: none !important;
  color: #ff9800 !important; /* 蓝灰色的反转色：橙色 */
}

button.zl-button.zl-button--login,
.zl-button.zl-button--login {
  background: linear-gradient(135deg, #a8c8ec, #c8b5db) !important;
  border: none !important;
  color: #4a148c !important; /* 淡蓝紫色的反转色：深紫色 */
  font-weight: 600 !important;
}

button.zl-button.zl-button--glass,
.zl-button.zl-button--glass {
  /* 优化毛玻璃效果：降低模糊强度但保持美观 */
  background: rgba(255, 255, 255, 0.22) !important;
  backdrop-filter: blur(6px) saturate(105%) !important;
  -webkit-backdrop-filter: blur(6px) saturate(105%) !important;
  border: 1px solid rgba(255, 255, 255, 0.45) !important;
  color: rgba(44, 62, 80, 0.9) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06) !important;
}

button.zl-button.zl-button--gradient,
.zl-button.zl-button--gradient {
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  border: none !important;
  color: #ffeb3b !important; /* 与primary相同的反转色 */
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3) !important;
}

/* 毛玻璃和渐变效果已合并到基础样式中 */

/* 圆角 */
.zl-button--rounded {
  border-radius: 12px !important;
}

/* 阴影 */
.zl-button--shadow {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

/* 悬停提升效果 - 优化版本：使用scale替代translateY */
.zl-button--hover-lift:hover {
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.15s ease !important;
}

/* 尺寸样式 */
.zl-button--mini {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
}

.zl-button--small {
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
}

.zl-button--medium {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
}

.zl-button--large {
  height: 48px;
  padding: 0 20px;
  font-size: 16px;
}

/* 特殊状态样式 - glass变体使用反转色，使用最强选择器 */
button.zl-button.zl-button--primary.zl-button--glass,
.zl-button.zl-button--primary.zl-button--glass {
  background: rgba(102, 126, 234, 0.15) !important;
  color: #ffeb3b !important; /* 保持反转色 */
  border-color: rgba(102, 126, 234, 0.3) !important;
}

button.zl-button.zl-button--success.zl-button--glass,
.zl-button.zl-button--success.zl-button--glass {
  background: rgba(79, 172, 254, 0.15) !important;
  color: #ff4500 !important; /* 保持反转色 */
  border-color: rgba(79, 172, 254, 0.3) !important;
}

button.zl-button.zl-button--warning.zl-button--glass,
.zl-button.zl-button--warning.zl-button--glass {
  background: rgba(255, 167, 38, 0.15) !important;
  color: #1a237e !important; /* 保持反转色 */
  border-color: rgba(255, 167, 38, 0.3) !important;
}

button.zl-button.zl-button--danger.zl-button--glass,
.zl-button.zl-button--danger.zl-button--glass {
  background: rgba(255, 87, 34, 0.15) !important;
  color: #00e676 !important; /* 保持反转色 */
  border-color: rgba(255, 87, 34, 0.3) !important;
}

button.zl-button.zl-button--info.zl-button--glass,
.zl-button.zl-button--info.zl-button--glass {
  background: rgba(66, 165, 245, 0.15) !important;
  color: #ff9800 !important; /* 保持反转色 */
  border-color: rgba(66, 165, 245, 0.3) !important;
}

button.zl-button.zl-button--secondary.zl-button--glass,
.zl-button.zl-button--secondary.zl-button--glass {
  background: rgba(168, 168, 168, 0.15) !important;
  color: #ffffff !important; /* 保持反转色 */
  border-color: rgba(168, 168, 168, 0.3) !important;
}

/* 登录按钮特殊样式 - 已合并到基础样式中 */

.zl-button--login:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(168, 200, 236, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--gradient:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--primary:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--secondary:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(168, 168, 168, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--success:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(79, 172, 254, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--warning:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(255, 167, 38, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--danger:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(255, 87, 34, 0.4) !important;
  transition: all 0.15s ease !important;
}

.zl-button--info:hover {
  /* 优化动画：轻微缩放替代位移 */
  transform: scale(1.02) !important;
  box-shadow: 0 8px 25px rgba(66, 165, 245, 0.4) !important;
  transition: all 0.15s ease !important;
}

/* 禁用状态 */
.zl-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .zl-button--large {
    height: 44px;
    font-size: 15px;
  }
  
  .zl-button--medium {
    height: 36px;
    font-size: 13px;
  }
  
  .zl-button--small {
    height: 28px;
    font-size: 12px;
  }
}
</style>